import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, SimpleGrid, Text, useToast } from '@chakra-ui/react'
import handleToast from '../HandleToast'
import ConfirmModal from '../common/ConfirmModal'
import ModalInput from '../forms/modalForms/modalInput'
import { CustomDateInput } from '../forms/modalForms/customDate'

interface DiscardModalProps {
  show: boolean
  children?: React.ReactNode

  handleClose: () => void
  notRow?: boolean
  handleDelete: () => void

  action: 'disable' | 'delete' | 'restore'
  deactivatedData?: { [key: string]: string }
  setDeactivatedData?: (data: { [key: string]: string }) => void
  selectedRowsCount: number
  isLoading?: boolean
  confirmDisabled?: boolean
}

const DiscardModal: React.FC<DiscardModalProps> = ({
  show,
  handleClose,
  handleDelete,
  action,
  selectedRowsCount,
  notRow,
  deactivatedData,
  setDeactivatedData,
  isLoading: isLoadingProp,
  confirmDisabled,
  children,
}) => {
  const { t } = useTranslation()
  const actionLabel =
    action === 'disable'
      ? t('common.actions.disable')
      : action === 'restore'
        ? t('common.actions.restore')
        : t('common.actions.delete')
  const itemLabel = t('common.labels.row', { count: selectedRowsCount })
  const toast = useToast()
  const [isLocalLoading, setIsLocalLoading] = React.useState(false)
  const isLoading = isLoadingProp ?? isLocalLoading

  const handleConfirm = async () => {
    if (isLoadingProp !== undefined) {
      // Parent manages loading state
      handleDelete()
    } else {
      setIsLocalLoading(true)
      try {
        handleDelete()
        handleClose()
      } catch (err) {
        handleToast(toast, false, t('common.states.error'), t('messages.deleteFailed'))
      } finally {
        setIsLocalLoading(false)
      }
    }
  }

  return (
    <ConfirmModal
      isOpen={show}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title={
        notRow
          ? t('common.modals.deleteItemTitle')
          : t('common.modals.actionCountTitle', {
              action: actionLabel,
              count: selectedRowsCount,
              item: itemLabel,
            })
      }
      message={
        notRow
          ? t('messages.confirmDelete')
          : t('common.modals.confirmAction', { count: selectedRowsCount, action: actionLabel })
      }
      description={
        action === 'disable'
          ? t('common.modals.disableHint')
          : action === 'delete'
            ? t('common.modals.deleteHint')
            : undefined
      }
      confirmLabel={actionLabel}
      isDestructive={action !== 'restore'}
      isLoading={isLoading}
      isConfirmDisabled={confirmDisabled}
    >
      {action === 'disable' && (
        <SimpleGrid columns={1} w="100%" mb="20px" gap={4} textAlign="left">
          <ModalInput
            label={t('workforce.people.deactivation.exitReasonLabel')}
            placeholder={t('workforce.people.deactivation.exitReasonPlaceholder')}
            type="text"
            name="deactive_reason"
            value={deactivatedData?.reason || ''}
            onChange={(e) => setDeactivatedData?.({ ...deactivatedData, reason: e.target.value })}
          />
          <Box width="100%">
            <Text textStyle="l-xs" color="solid.500" mb={1}>
              {t('workforce.people.deactivation.lastDayLabel')}
            </Text>
            <CustomDateInput
              onChange={(selectedDate) =>
                setDeactivatedData?.({
                  ...deactivatedData,
                  last_day: selectedDate
                    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                    : '',
                })
              }
            />
          </Box>
          <ModalInput
            label={t('workforce.people.deactivation.exitEvaluationLabel')}
            placeholder={t('workforce.people.deactivation.exitEvaluationPlaceholder')}
            type="text"
            name="status_exit_evaluation"
            value={deactivatedData?.status_exit_evaluation || ''}
            onChange={(e) => setDeactivatedData?.({ ...deactivatedData, status_exit_evaluation: e.target.value })}
          />
          <ModalInput
            label={t('workforce.people.deactivation.statusAccessITLabel')}
            placeholder={t('workforce.people.deactivation.statusAccessITPlaceholder')}
            type="text"
            name="status_access_it"
            value={deactivatedData?.status_access_it || ''}
            onChange={(e) => setDeactivatedData?.({ ...deactivatedData, status_access_it: e.target.value })}
          />
          <ModalInput
            label={t('workforce.people.deactivation.statusKeysLabel')}
            placeholder={t('workforce.people.deactivation.statusKeysPlaceholder')}
            type="text"
            name="status_keys"
            value={deactivatedData?.status_keys || ''}
            onChange={(e) => setDeactivatedData?.({ ...deactivatedData, status_keys: e.target.value })}
          />
          <ModalInput
            label={t('workforce.people.deactivation.statusCompanyDevicesLabel')}
            placeholder={t('workforce.people.deactivation.statusCompanyDevicesPlaceholder')}
            type="text"
            name="status_company_devices"
            value={deactivatedData?.status_company_devices || ''}
            onChange={(e) => setDeactivatedData?.({ ...deactivatedData, status_company_devices: e.target.value })}
          />
        </SimpleGrid>
      )}
      {children}
    </ConfirmModal>
  )
}

export default DiscardModal
